import { AccountFilterSidebar, FilterSection } from "@/components/accounts/AccountFilterSidebar";
import type { TargetUniverseFilters as Filters, TargetUniverseStats } from "@/hooks/useTargetUniverse";
import { useMemo } from "react";
import {
  ACCOUNT_TIER_ORDER,
  ICP_TIER_ORDER,
  EMPLOYEE_RANGE_ORDER,
} from "@/config/filters";

interface TargetUniverseFiltersProps {
  totalCount: number;
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  filterOptions: {
    categories: { value: string; label: string; count: number }[];
    subcategories: { value: string; label: string; count: number }[];
    countries: { value: string; label: string; count: number }[];
    territories: { value: string; label: string; count: number }[];
    carrModalities: { value: string; label: string; count: number }[];
    modalities: { value: string; label: string; count: number }[];
    therapeuticAreas: { value: string; label: string; count: number }[];
    clinicalPhases: { value: string; label: string; count: number }[];
    fundingStatuses: { value: string; label: string; count: number }[];
    employeeRanges: { value: string; label: string; count: number }[];
    accountTiers: { value: string; label: string; count: number }[];
    icpTiers: { value: string; label: string; count: number }[];
  } | undefined;
  stats?: TargetUniverseStats | null;
}

const TIER_OPTIONS = [
  { value: "new", label: "New (Unreviewed)" },
  { value: "cold_target", label: "Cold Target" },
  { value: "warm_target", label: "Warm Target" },
  { value: "active_prospect", label: "Active Prospect" },
  { value: "customer", label: "Customer" },
  { value: "disqualified", label: "Disqualified" },
];

const ICP_TIER_OPTIONS = ICP_TIER_ORDER.map((t) => ({
  value: t,
  label: t,
}));

const CONTACTS_OPTIONS = [
  { value: "has_contacts", label: "Has Contacts" },
  { value: "no_contacts", label: "No Contacts" },
];

export function TargetUniverseFilterSidebar({
  totalCount,
  filters,
  onFiltersChange,
  filterOptions,
  stats,
}: TargetUniverseFiltersProps) {
  const filterValues: Record<string, string[]> = {
    contacts: filters.hasContacts === true ? ["has_contacts"] : filters.hasContacts === false ? ["no_contacts"] : [],
    territory: filters.territory,
    country: filters.country,
    carrModality: filters.carrModality,
    modality: filters.modality,
    therapeuticArea: filters.therapeuticArea,
    clinicalPhase: filters.clinicalPhase,
    fundingStatus: filters.fundingStatus,
    employeeRange: filters.employeeRange,
    accountTier: filters.accountTier,
    icpTier: filters.icpTier,
  };

  const toggleValues: Record<string, boolean> = {
    topAccount: filters.topAccount || false,
    notesStale: filters.notesStale || false,
    needsScaleReview: filters.needsScaleReview || false,
  };

  const filterSections: FilterSection[] = useMemo(
    () => [
      { id: "accountTier", label: "Account Status", options: (() => {
        const facetMap = new Map((filterOptions?.accountTiers || []).map((t) => [t.value, t.count]));
        return TIER_OPTIONS.map((t) => ({ ...t, count: facetMap.get(t.value) || 0 }));
      })() },
      { id: "icpTier", label: "ICP Tier", options: filterOptions?.icpTiers || ICP_TIER_OPTIONS },
      { id: "territory", label: "Territory", options: [
        ...(filterOptions?.territories || []),
        { value: "__unassigned__", label: "Not Assigned" },
      ] },
      { id: "carrModality", label: "Modality Type", options: filterOptions?.carrModalities || [] },
      { id: "modality", label: "Cell Type", options: filterOptions?.modalities || [] },
      { id: "therapeuticArea", label: "Therapeutic Area", options: filterOptions?.therapeuticAreas || [] },
      { id: "clinicalPhase", label: "Clinical Phase", options: filterOptions?.clinicalPhases || [] },
      { id: "fundingStatus", label: "Funding Status", options: filterOptions?.fundingStatuses || [] },
      { id: "employeeRange", label: "Company Size", options: (filterOptions?.employeeRanges || []).sort(
        (a, b) => EMPLOYEE_RANGE_ORDER.indexOf(a.value) - EMPLOYEE_RANGE_ORDER.indexOf(b.value)
      ) },
      { id: "country", label: "Country", options: filterOptions?.countries || [] },
      { id: "contacts", label: "Contacts", options: CONTACTS_OPTIONS },
      { id: "topAccount", label: "Top Accounts", type: "toggle" as const, options: [], toggleKey: "topAccount", toggleLabel: "⭐ Top Accounts Only" },
      {
        id: "notesStale",
        label: "Review Quality",
        type: "toggle" as const,
        options: [],
        toggleKey: "notesStale",
        toggleLabel: `🕒 Stale notes (>180d)${stats?.notes_stale ? ` (${stats.notes_stale.toLocaleString()})` : ""}`,
      },
      {
        id: "needsScaleReview",
        label: "",
        type: "toggle" as const,
        options: [],
        toggleKey: "needsScaleReview",
        toggleLabel: `⚠️ Small scale (<6 employees)${stats?.needs_scale_review ? ` (${stats.needs_scale_review.toLocaleString()})` : ""}`,
      },
    ],
    [filterOptions, stats]
  );

  const handleFilterChange = (sectionId: string, values: string[]) => {
    const updated = { ...filters };
    if (sectionId === "contacts") {
      if (values.includes("has_contacts") && !values.includes("no_contacts")) {
        updated.hasContacts = true;
      } else if (values.includes("no_contacts") && !values.includes("has_contacts")) {
        updated.hasContacts = false;
      } else {
        updated.hasContacts = null;
      }
    } else {
      (updated as any)[sectionId] = values;
    }
    onFiltersChange(updated);
  };

  const handleToggleChange = (key: string, value: boolean) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const hasActiveFilters =
    filters.search.length > 0 ||
    filters.hasContacts !== null ||
    filters.territory.length > 0 ||
    filters.carrModality.length > 0 ||
    filters.modality.length > 0 ||
    filters.therapeuticArea.length > 0 ||
    filters.clinicalPhase.length > 0 ||
    filters.fundingStatus.length > 0 ||
    filters.employeeRange.length > 0 ||
    filters.accountTier.length > 0 ||
    filters.icpTier.length > 0 ||
    filters.topAccount ||
    filters.notesStale ||
    filters.needsScaleReview ||
    filters.country.length > 0;

  const clearFilters = () => {
    onFiltersChange({
      search: "",
      category: [],
      subcategory: [],
      hasContacts: null,
      country: [],
      territory: [],
      carrModality: [],
      modality: [],
      therapeuticArea: [],
      clinicalPhase: [],
      fundingStatus: [],
      employeeRange: [],
      accountTier: [],
      icpTier: [],
      topAccount: false,
      notesStale: false,
      needsScaleReview: false,
    });
  };

  return (
    <AccountFilterSidebar
      totalCount={totalCount}
      searchPlaceholder="Search by company name..."
      searchValue={filters.search}
      onSearchChange={(value) => onFiltersChange({ ...filters, search: value })}
      filterSections={filterSections}
      filterValues={filterValues}
      onFilterChange={handleFilterChange}
      toggleValues={toggleValues}
      onToggleChange={handleToggleChange}
      onClearFilters={clearFilters}
      hasActiveFilters={hasActiveFilters}
    />
  );
}
